import { cx } from '@flowus/common/cx';
import type { FC } from 'react';
import { Button } from 'src/common/components/button';
import { useNewUserTask } from 'src/hooks/activities/use-activity';
import type { ActivityDetail } from 'src/services/activities/type';
import { numberToPercent } from 'src/utils/number';
import { MessageBoardType } from './type';
import { useMessageInfo } from './use-message-info';

interface Props {
  messageType: MessageBoardType;
  onCloseModal: () => void;
  overlap?: boolean;
}

export const MessageCard: FC<Props> = ({ messageType, onCloseModal, overlap }) => {
  const messageInfo = useMessageInfo(onCloseModal);

  const message = messageInfo[messageType];
  if (!message) return null;

  const content = (
    <div
      onClick={() => {
        onCloseModal();
        message.onClick?.();
      }}
      className={cx(
        'relative z-20 pt-4 pb-1 px-2.5 border mb-5 rounded-md bg-white1',
        message.onClick && 'cursor-pointer'
      )}
      style={{ boxShadow: '0px 1px 3px var(--black_006)' }}
    >
      <div className="flex">
        <span className="w-6 h-6 flex items-center justify-center p-1.5 bg-grey9 rounded">
          {message.icon}
        </span>
        <div className="ml-2 flex flex-col">
          <h2 className="text-t2-medium">{message.title}</h2>
          <div className="mt-0.5 text-t4 text-grey3">{message.text}</div>
        </div>
      </div>

      {message?.buttonText && (
        <Button
          colorType="secondary"
          className="h-8 mt-5 w-full border-grey5"
          onClick={message.buttonClick}
        >
          {message.buttonText}
        </Button>
      )}

      {messageType === MessageBoardType.NEW_USER_TASK && <NewUserTaskProgress />}
    </div>
  );

  if (overlap) {
    return (
      <div className="relative">
        {content}
        <div className="absolute w-[96%] h-10 rounded-md text-grey6 left-[2%] -bottom-1.5 bg-white1 z-10 border-grey6 border" />
        <div className="absolute w-[92%] h-10 rounded-md text-grey6 left-[4%] -bottom-3 bg-white1 border-grey6 border" />
      </div>
    );
  }

  return content;
};

const NewUserTaskProgress = () => {
  const { completedTaskLength, allTaskLength, progress, basicList, advancedList } =
    useNewUserTask();

  let nextTask: ActivityDetail | undefined = undefined;
  if (progress !== 1) {
    nextTask = basicList.find((item) => item.status === 'doing');
    if (!nextTask) {
      nextTask = advancedList.find((item) => item.status === 'doing');
    }
  }

  return (
    <div className="mt-5">
      <div className="relative w-full h-[5px] bg-grey9 rounded overflow-hidden">
        <div
          className="absolute left-0 h-full bg-active_color"
          style={{ width: numberToPercent(progress) }}
        />
      </div>
      <div className="flex items-center mt-2.5">
        {progress !== 1 && nextTask && (
          <>
            <span className="text-grey3 text-t4 text-ellipsis">{nextTask.name}</span>
            <span className="text-t4-medium text-active_color mx-0.5">+{nextTask.score}</span>
          </>
        )}
        <span className="ml-auto text-t4 text-grey3">
          {completedTaskLength}/{allTaskLength}
        </span>
      </div>
    </div>
  );
};
